import React, { useEffect, useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Sheet from '@amzn/meridian/sheet';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Text from '@amzn/meridian/text';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import RadioButton from '@amzn/meridian/radio-button';
import styles from './goals.module.css';
import { getFutureWeeks } from '../../Util/Week';
import {
  getGoalMetricsForVertical,
  GoalMetricMetadata,
  GoalMetricUnit,
  VerticalOption,
  validateGoalMetricValue,
  GoalMetricOption,
  GoalLevel,
  GoalPeriodType,
} from '@amzn/aet-galactus-kit';
import {
  getMetricStringIdAndDefaultMessage,
  getVerticalOptionFromValue,
  Toast,
  GOAL_DESCRIPTION_SIZE_LIMIT,
  GoalCreationInputs,
  GoalPage,
  getRecentMonths,
  filterGoalMetricsByFeatureFlags,
  filterCCXGoalMetrics,
  formatGoalTargetValueForAPI,
} from './helper';
import { Profile } from '../../Types/Profile';
import DataSource from '../../api';
import DataState from '../../Types/DataState';
import { CreateGoalResponse } from '../../api/Goals/schema';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { StateContext } from '../../GlobalStateStore';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';

export interface CreateGoalProps {
  lockedGoalLevel?: boolean;
  defaultAssociateID?: string;
  cscEntityPrefix: string;
  profile: Profile; // must always pass TM profile
  changeFlag: any;
  isOpen: boolean;
  setIsOpen: any;
  page: GoalPage;
}

const CreateGoal: React.FC<CreateGoalProps> = ({
  lockedGoalLevel = false,
  defaultAssociateID,
  cscEntityPrefix,
  profile,
  changeFlag,
  isOpen,
  setIsOpen,
  page,
}: CreateGoalProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const intl = useIntl();
  const goalSettingTeamEnabled = isFeatureEnabled(Feature.GOAL_SETTING_TEAM);
  const enableACHTHoldTime = isFeatureEnabled(
    Feature.ENABLE_GOAL_SETTING_ACHT_HOLD_TIME
  );
  const [goalType, setGoalType] = useState(
    !goalSettingTeamEnabled || lockedGoalLevel ? GoalLevel.Associate : ''
  );

  const [goalDescription, setGoalDescription] = useState('');
  const [vertical, setVertical] = useState('');
  const [metric, setMetric] = useState('');
  const [selectedMetric, setSelectedMetric] = useState<GoalMetricMetadata>();
  const [targetValue, setTargetValue] = useState('');
  const [primaryUnit, setPrimaryUnit] = useState('');
  const [secondaryUnit, setSecondaryUnit] = useState('');
  const [timeValue, setTimeValue] = useState();
  const [goalPeriodType, setGoalPeriodType] = useState<GoalPeriodType>(
    GoalPeriodType.Week
  );
  const [weekList, setWeekList] = useState<string[]>([]);
  const [monthList, setMonthList] = useState<
    { year: number; month: string; monthNum: number }[]
  >([]);
  const [metricList, setMetricList] = useState<GoalMetricMetadata[]>([]);
  const [selectedAssociate, setSelectedAssociate] = useState('');
  const [createGoal, setCreateGoal] = useState<CreateGoalResponse>();
  const [createGoalState, setCreateGoalState] = useState(DataState.INITIALIZED);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [verificationFailList, setVerificationFailList] = useState<string[]>(
    []
  );
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isAdmin, requesterUserID, requesterUsername } =
    inspireOptions.data;
  const selectedOrDefaultAssociate =
    cscEntityPrefix && defaultAssociateID
      ? `${cscEntityPrefix}:${defaultAssociateID}`
      : selectedAssociate;

  const onClickClose = () => {
    setIsOpen(false);
    // reset all filled values
    setGoalType(
      !goalSettingTeamEnabled || lockedGoalLevel ? GoalLevel.Associate : ''
    );
    setSelectedAssociate('');
    setGoalDescription('');
    setVertical('');
    setMetric('');
    setSelectedMetric(undefined);
    setTargetValue('');
    setPrimaryUnit('');
    setSecondaryUnit('');
    setTimeValue(undefined);
    setGoalPeriodType(GoalPeriodType.Week);
    setVerificationFailList([]);
  };
  const onClose = () => setIsOpen(false);

  let toastId = 0;
  const onCloseToast = (id: string) =>
    setToasts(toasts.filter(t => t.id !== id));

  const onOpenToast = (type: 'success' | 'error') => {
    const id = `${++toastId}`;
    setToasts(toasts.concat({ id, type }));
    setTimeout(() => onCloseToast(id), 5000);
  };

  const commonClickstreamData = {
    'data-csa-c-type': 'button',
    'data-csa-c-tag1': page,
    'data-csa-c-tag2': 'Goal Setting',
    'data-csa-c-tag3': 'Goal Creation',
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  };

  const handleCreateGoal = async () => {
    const apiTargetValue = formatGoalTargetValueForAPI(
      selectedMetric,
      targetValue,
      primaryUnit,
      secondaryUnit
    );

    // input validation
    const localVerificationFailList: string[] = [];
    if (!goalType) {
      localVerificationFailList.push(GoalCreationInputs.GOAL_TYPE);
    }
    if (!metric) {
      localVerificationFailList.push(GoalCreationInputs.METRIC);
    }
    if (goalType === GoalLevel.Associate && !selectedOrDefaultAssociate) {
      localVerificationFailList.push(GoalCreationInputs.ASSOCIATE);
    }
    if (
      goalDescription.length == 0 ||
      goalDescription.length > GOAL_DESCRIPTION_SIZE_LIMIT
    ) {
      localVerificationFailList.push(GoalCreationInputs.GOAL_DESCRIPTION);
    }
    if (!vertical) {
      localVerificationFailList.push(GoalCreationInputs.VERTICAL);
    }
    if (!timeValue) {
      localVerificationFailList.push(GoalCreationInputs.TIME_VALUE);
    }

    if (selectedMetric?.metricUnit === GoalMetricUnit.PERCENTAGE) {
      if (targetValue.length < 1) {
        localVerificationFailList.push(GoalCreationInputs.TARGET_VALUE);
      }
    } else {
      if (!primaryUnit && !secondaryUnit) {
        localVerificationFailList.push(GoalCreationInputs.TARGET_VALUE);
      }
    }

    if (metric && apiTargetValue.length > 0) {
      if (
        !validateGoalMetricValue(metric as GoalMetricOption, apiTargetValue)
      ) {
        localVerificationFailList.push(GoalCreationInputs.TARGET_VALUE);
      } else if (
        selectedMetric?.metricUnit === GoalMetricUnit.PERCENTAGE &&
        (Number(apiTargetValue) > 100 || Number(apiTargetValue) < 0)
      ) {
        localVerificationFailList.push(GoalCreationInputs.TARGET_VALUE);
      }
    }
    if (localVerificationFailList.length > 0) {
      setVerificationFailList(localVerificationFailList);
      return;
    }

    const queryVariables = apiNaws.prepareCreateGoalInput(
      goalType == GoalLevel.Team
        ? `${profile.user.userID}`
        : `${selectedOrDefaultAssociate.split(':')[1]}`,
      cscEntityPrefix,
      goalType == GoalLevel.Team
        ? `${profile.user.userName}`
        : isAdmin
          ? [...profile.directReports, ...profile.sharedProfileCoachees].filter(
              report =>
                `${cscEntityPrefix}:${report.userID}` ===
                selectedOrDefaultAssociate
            )[0].userName
          : profile.directReports.filter(
              report =>
                `${cscEntityPrefix}:${report.userID}` ===
                selectedOrDefaultAssociate
            )[0].userName,
      vertical,
      goalType,
      goalDescription,
      metric,
      apiTargetValue,
      goalPeriodType,
      timeValue,
      profile.user.alias // always be a TM alias
    );

    await apiNaws.createGoal(queryVariables, setCreateGoal, setCreateGoalState);
  };

  useEffect(() => {
    if (createGoalState === DataState.LOADED) {
      onOpenToast('success');
      onClickClose();
      changeFlag();
    }
    if (createGoalState === DataState.ERROR) onOpenToast('error');
  }, [createGoalState]);

  useEffect(() => {
    if (vertical === '') return;

    const verticalOption = getVerticalOptionFromValue(vertical);
    if (verticalOption) {
      // Filter metrics based on feature flags
      let metricsList = filterGoalMetricsByFeatureFlags(
        getGoalMetricsForVertical(verticalOption),
        enableACHTHoldTime
      );

      // Filter out CCX-related metrics when enableCCXRemoval is enabled
      metricsList = filterCCXGoalMetrics(metricsList);

      setMetric('');
      setMetricList(metricsList);
    } else {
      setMetric('');
      setMetricList([]);
    }
  }, [vertical]);

  useEffect(() => {
    if (!metric) return;
    const selectedGoalMetric = metricList.find(
      metricOptionItem => metricOptionItem.metricName === metric
    );
    setSelectedMetric(selectedGoalMetric);
    setTargetValue('');
    setPrimaryUnit('');
    setSecondaryUnit('');
  }, [metric]);

  useEffect(() => {
    if (goalPeriodType === GoalPeriodType.Month) {
      setMonthList(getRecentMonths(4));
    } else {
      setWeekList(getFutureWeeks(13));
    }
    setTimeValue(undefined);
  }, [goalPeriodType]);

  return (
    <>
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {toast => (
          <>
            {toast.type === 'success' && (
              <Alert toast={true} onClose={toast.onClose} type='success'>
                <FormattedMessage
                  id='cs_perfportal_goal_add_success'
                  defaultMessage='A new goal was successfully created.'
                />
              </Alert>
            )}
            {toast.type === 'error' && (
              <Alert toast={true} onClose={toast.onClose} type='error'>
                <FormattedMessage
                  id='cs_perfportal_goal_add_fail'
                  defaultMessage="System error. The goal wasn't created.  Try Again."
                />
              </Alert>
            )}
          </>
        )}
      </Toaster>
      <Column
        alignmentVertical='top'
        heights='fill'
        data-cy='create-a-new-goal-sheet'
      >
        <Sheet type='push' side='end' open={isOpen} onClose={onClose}>
          <Column spacing='400' width='320px' maxWidth='320px'>
            <Row widths={[207, 'fill', 40]} alignmentHorizontal='end'>
              <Text type='h400'>
                <FormattedMessage
                  id='cs_perfportal_goals_create_a_new_goal'
                  defaultMessage='Create a New Goal'
                />
              </Text>
              <span></span>
              <Button
                onClick={onClickClose}
                type='icon'
                data-csa-c-slot-id={`${page}-goal-setting-goal-creation-close-goal`}
                data-csa-c-content-id={`${page}-goal-setting-goal-creation-close-goal-for${
                  selectedOrDefaultAssociate ?? requesterUsername
                }`}
                {...commonClickstreamData}
              >
                <Icon tokens={closeSmallTokens} />
              </Button>
            </Row>
            <span style={{ maxWidth: 280 }}>
              <Text type='b400'>
                <FormattedMessage
                  id='cs_perfportal_goals_promote_growth_while_achievable'
                  defaultMessage='Goals should promote growth while being achievable.'
                />
              </Text>
            </span>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
              <FormattedMessage
                id='cs_perfportal_goal_creation_details'
                defaultMessage='Details'
              />
            </span>
            {goalSettingTeamEnabled && (
              <Select
                label={intl.formatMessage({
                  id: 'cs_perfportal_goals_goal_type_required',
                  defaultMessage: 'Goal Type*',
                })}
                value={goalType}
                onChange={setGoalType}
                width={280}
                disabled={lockedGoalLevel}
                error={verificationFailList.includes(
                  GoalCreationInputs.GOAL_TYPE
                )}
                data-testid='select-goal-type'
                size='xlarge'
              >
                <SelectOption
                  value='Team'
                  label={intl.formatMessage({
                    id: 'cs_perfportal_team',
                    defaultMessage: 'Team',
                  })}
                />
                <SelectOption
                  value='Associate'
                  label={intl.formatMessage({
                    id: 'cs_perfportal_associate',
                    defaultMessage: 'Associate',
                  })}
                />
              </Select>
            )}
            {goalType === GoalLevel.Associate && (
              <Select
                label={intl.formatMessage({
                  id: 'cs_perfportal_name_required',
                  defaultMessage: 'Name*',
                })}
                value={selectedOrDefaultAssociate}
                onChange={setSelectedAssociate}
                width={280}
                data-testid='select-associate'
                disabled={lockedGoalLevel}
                error={verificationFailList.includes(
                  GoalCreationInputs.ASSOCIATE
                )}
                size='xlarge'
              >
                {isAdmin
                  ? [
                      ...new Set([
                        ...profile.directReports,
                        ...profile.sharedProfileCoachees,
                      ]),
                    ].map(report => (
                      <SelectOption
                        key={`${cscEntityPrefix}:${report.userID}`}
                        value={`${cscEntityPrefix}:${report.userID}`}
                        label={report.displayName}
                      />
                    ))
                  : profile.directReports.map(report => (
                      <SelectOption
                        key={`${cscEntityPrefix}:${report.userID}`}
                        value={`${cscEntityPrefix}:${report.userID}`}
                        label={report.displayName}
                      />
                    ))}
              </Select>
            )}
            <Input
              id='selectGoalDescription'
              value={goalDescription}
              onChange={setGoalDescription}
              type='text'
              label={intl.formatMessage({
                id: 'cs_perfportal_goal_description_required',
                defaultMessage: 'Goal Description*',
              })}
              width={280}
              error={
                goalDescription.length > GOAL_DESCRIPTION_SIZE_LIMIT ||
                verificationFailList.includes(
                  GoalCreationInputs.GOAL_DESCRIPTION
                )
              }
              size='xlarge'
            />
            {goalDescription.length > GOAL_DESCRIPTION_SIZE_LIMIT && (
              <Alert width='280px' size='small' type='error'>
                {intl.formatMessage({
                  id: 'cs_perfportal_goal_description_limit',
                  defaultMessage:
                    'Goal descriptions should be 50 characters or less.',
                })}
              </Alert>
            )}
            <Column width='280px' maxWidth='280'>
              <span style={{ maxWidth: 280 }}>
                <Text type='b200' color='secondary'>
                  <FormattedMessage
                    id='cs_perfportal_goals_write_a_description_thats_clear'
                    defaultMessage="Write a description that's short and clear, like 'Reduce silent
        time.'"
                  />
                </Text>
              </span>
            </Column>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
              <FormattedMessage
                id='cs_perfportal_goal_target'
                defaultMessage='Target'
              ></FormattedMessage>
            </span>
            <Select
              label={intl.formatMessage({
                id: 'cs_perfportal_vertical_required',
                defaultMessage: 'Vertical*',
              })}
              value={vertical}
              onChange={setVertical}
              width={280}
              error={verificationFailList.includes(GoalCreationInputs.VERTICAL)}
              data-cy='goal-vertical'
              size='xlarge'
            >
              <SelectOption
                value={VerticalOption.AB_V2}
                label='Amazon Business'
              />
              <SelectOption value={VerticalOption.CAP_V2} label='CAP' />
              <SelectOption value={VerticalOption.D2_V2} label='D2AS' />
              <SelectOption
                value={VerticalOption.Consumer_V2}
                label='Consumer'
              />
              <SelectOption value={VerticalOption.SDS_V2} label='SDS' />
              <SelectOption value={VerticalOption.Total_V2} label='Total' />
            </Select>
            <Select
              label={intl.formatMessage({
                id: 'cs_perfportal_metric_required',
                defaultMessage: 'Metric*',
              })}
              disabled={vertical == ''}
              value={metric}
              onChange={setMetric}
              width={280}
              error={verificationFailList.includes(GoalCreationInputs.VERTICAL)}
              data-cy='goal-metrics'
              size='xlarge'
            >
              {metricList.map(metricOption => {
                return (
                  <SelectOption
                    key={metricOption.metricName}
                    value={metricOption.metricName}
                    label={intl.formatMessage({
                      ...getMetricStringIdAndDefaultMessage(
                        metricOption.metricName
                      ),
                    })}
                  />
                );
              })}
            </Select>
            <Text type='b200'>
              {selectedMetric?.isHigherBetter
                ? intl.formatMessage({
                    id: 'cs_perfportal_greater_than_or_equal_to',
                    defaultMessage: 'Greater than or equal to',
                  })
                : intl.formatMessage({
                    id: 'cs_perfportal_less_than_or_equal_to',
                    defaultMessage: 'Less than or equal to',
                  })}
            </Text>
            {selectedMetric?.metricUnit === GoalMetricUnit.MIN_SEC ||
            selectedMetric?.metricUnit === GoalMetricUnit.HOUR_MIN ? (
              <Row spacing='200' alignmentHorizontal='start'>
                <Input
                  id='inputMinutes'
                  value={primaryUnit}
                  onChange={setPrimaryUnit}
                  type='number'
                  width={130}
                  label={
                    selectedMetric?.metricUnit === GoalMetricUnit.HOUR_MIN
                      ? intl.formatMessage({
                          id: 'cs_perfportal_hours',
                          defaultMessage: 'Hours',
                        })
                      : intl.formatMessage({
                          id: 'cs_perfportal_minutes',
                          defaultMessage: 'Minutes',
                        })
                  }
                  error={verificationFailList.includes(
                    GoalCreationInputs.TARGET_VALUE
                  )}
                  disabled={metric == ''}
                  size='xlarge'
                />
                <Input
                  id='inputSeconds'
                  value={secondaryUnit}
                  onChange={setSecondaryUnit}
                  type='number'
                  width={130}
                  label={
                    selectedMetric?.metricUnit === GoalMetricUnit.HOUR_MIN
                      ? intl.formatMessage({
                          id: 'cs_perfportal_minutes',
                          defaultMessage: 'Minutes',
                        })
                      : intl.formatMessage({
                          id: 'cs_perfportal_seconds',
                          defaultMessage: 'Seconds',
                        })
                  }
                  error={verificationFailList.includes(
                    GoalCreationInputs.TARGET_VALUE
                  )}
                  disabled={metric == ''}
                  size='xlarge'
                />
              </Row>
            ) : (
              <Input
                id='inputTargetValue'
                value={targetValue}
                onChange={setTargetValue}
                type='number'
                width={280}
                label={intl.formatMessage({
                  id: 'cs_perfportal_goal_value_required',
                  defaultMessage: 'Value*',
                })}
                suffix={
                  selectedMetric?.metricUnit === GoalMetricUnit.PERCENTAGE
                    ? '%'
                    : ''
                }
                error={verificationFailList.includes(
                  GoalCreationInputs.TARGET_VALUE
                )}
                disabled={metric == ''}
                size='xlarge'
              />
            )}
            {verificationFailList.includes(GoalCreationInputs.TARGET_VALUE) && (
              <Alert width='280px' size='small' type='error'>
                {selectedMetric?.metricUnit === GoalMetricUnit.PERCENTAGE
                  ? intl.formatMessage({
                      id: 'cs_perfportal_goal_valid_target_percentage',
                      defaultMessage:
                        'Enter a whole number or a one digit decimal between 0 and 100.',
                    })
                  : selectedMetric?.metricUnit === GoalMetricUnit.HOUR_MIN
                    ? intl.formatMessage({
                        id: 'cs_perfportal_goal_valid_target_hour_min_separate',
                        defaultMessage: 'Please enter valid hours and minutes.',
                      })
                    : intl.formatMessage({
                        id: 'cs_perfportal_goal_valid_target_min_sec_separate',
                        defaultMessage:
                          'Please enter valid minutes and seconds.',
                      })}
              </Alert>
            )}
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
              <FormattedMessage
                id='cs_perfportal_goal_time_range'
                defaultMessage='Time Range'
              ></FormattedMessage>
            </span>
            <div className={styles.timerange}>
              <Row>
                <RadioButton
                  checked={goalPeriodType === GoalPeriodType.Week}
                  onChange={setGoalPeriodType}
                  name='selectTime'
                  value={GoalPeriodType.Week}
                  size='medium'
                >
                  <FormattedMessage
                    id='cs_perfportal_week'
                    defaultMessage='Week'
                  ></FormattedMessage>
                </RadioButton>
                <RadioButton
                  checked={goalPeriodType === GoalPeriodType.Month}
                  onChange={setGoalPeriodType}
                  name='selectTime'
                  value={GoalPeriodType.Month}
                  size='medium'
                >
                  <FormattedMessage
                    id='cs_perfportal_month'
                    defaultMessage='Month'
                  ></FormattedMessage>
                </RadioButton>
              </Row>{' '}
            </div>
            <Select
              label={
                goalPeriodType === GoalPeriodType.Week
                  ? intl.formatMessage({
                      id: 'cs_perfportal_goal_target_week',
                      defaultMessage: 'Target Week*',
                    })
                  : intl.formatMessage({
                      id: 'cs_perfportal_goal_target_month',
                      defaultMessage: 'Target Month*',
                    })
              }
              value={timeValue}
              onChange={setTimeValue}
              width={280}
              error={verificationFailList.includes(
                GoalCreationInputs.TIME_VALUE
              )}
              data-cy='goal-target-week'
              size='xlarge'
            >
              {goalPeriodType === GoalPeriodType.Week
                ? weekList.map(time => (
                    <SelectOption key={time} value={time} label={time} />
                  ))
                : goalPeriodType === GoalPeriodType.Month
                  ? monthList.map(({ year, month, monthNum }, index) => {
                      return (
                        <SelectOption
                          key={`${year}-${monthNum}`}
                          value={`${year}-${monthNum}`}
                          label={`${year} ${intl.formatMessage({
                            id: `cs_perfportal_time_month_${month.toLowerCase()}`,
                            defaultMessage: `${month}`,
                          })}`}
                        />
                      );
                    })
                  : null}
            </Select>
            <span style={{ maxWidth: 280 }}>
              <Text type='b200' color='secondary'>
                {goalPeriodType === GoalPeriodType.Week
                  ? intl.formatMessage({
                      id: 'cs_perfportal_time_calculated_entire_week',
                      defaultMessage:
                        'Metric results will be calculated using data from the entire Target Week.',
                    })
                  : intl.formatMessage({
                      id: 'cs_perfportal_time_calculated_entire_month',
                      defaultMessage:
                        'Metric results will be calculated using data from the entire Target Month.',
                    })}
              </Text>
            </span>
            <Row width='280px' alignmentHorizontal='end' spacing='300'>
              <Button
                onClick={onClickClose}
                type='secondary'
                data-csa-c-slot-id={`${page}-goal-setting-goal-creation-cancel-goal`}
                data-csa-c-content-id={`${page}-goal-setting-goal-creation-cancel-goal-for${
                  selectedOrDefaultAssociate ?? requesterUsername
                }`}
                {...commonClickstreamData}
              >
                <FormattedMessage
                  id='cs_perfportal_cancel'
                  defaultMessage='Cancel'
                ></FormattedMessage>
              </Button>
              <Button
                disabled={createGoalState === DataState.LOADING}
                id='saveButton'
                type='primary'
                onClick={handleCreateGoal}
                data-csa-c-slot-id={`${page}-goal-setting-goal-creation-create-goal`}
                data-csa-c-content-id={`${page}-goal-setting-goal-creation-create-goal-for${
                  selectedOrDefaultAssociate ?? requesterUsername
                }`}
                {...commonClickstreamData}
              >
                {createGoalState === DataState.LOADING ? (
                  <Loader size='small' />
                ) : (
                  <FormattedMessage
                    id='cs_perfportal_create'
                    defaultMessage='Create'
                  />
                )}
              </Button>
            </Row>
          </Column>
        </Sheet>
      </Column>
    </>
  );
};

export default CreateGoal;
